import { useEffect } from "react";

/**
 * Set per-route SEO basics on a CRA SPA without server rendering. Crawlers that
 * execute JS (Googlebot does, by default) will see these updates; static-only
 * crawlers fall back to whatever's in public/index.html.
 *
 * - title: full page title (set verbatim)
 * - description: meta description (creates the tag if missing)
 * - canonical: canonical URL (creates the tag if missing)
 */
export function useDocumentMeta({ title, description, canonical, ogImage }) {
  useEffect(() => {
    if (typeof document === "undefined") return;

    const previousTitle = document.title;
    if (title) document.title = title;

    const setOrCreateMeta = (selector, attrs) => {
      let el = document.head.querySelector(selector);
      if (!el) {
        el = document.createElement("meta");
        Object.entries(attrs).forEach(([k, v]) => v != null && el.setAttribute(k, v));
        document.head.appendChild(el);
        return { el, created: true };
      }
      const previous = {};
      Object.entries(attrs).forEach(([k, v]) => {
        previous[k] = el.getAttribute(k);
        if (v != null) el.setAttribute(k, v);
      });
      return { el, created: false, previous };
    };

    const setOrCreateLink = (rel, href) => {
      let el = document.head.querySelector(`link[rel="${rel}"]`);
      if (!el) {
        el = document.createElement("link");
        el.setAttribute("rel", rel);
        if (href) el.setAttribute("href", href);
        document.head.appendChild(el);
        return { el, created: true };
      }
      const previous = el.getAttribute("href");
      if (href) el.setAttribute("href", href);
      return { el, created: false, previous };
    };

    const restorers = [];

    if (description) {
      const { el, created, previous } = setOrCreateMeta('meta[name="description"]', {
        name: "description",
        content: description,
      });
      restorers.push(() => {
        if (created) el.remove();
        else if (previous?.content != null) el.setAttribute("content", previous.content);
      });

      const { el: ogEl, created: ogCreated, previous: ogPrev } = setOrCreateMeta('meta[property="og:description"]', {
        property: "og:description",
        content: description,
      });
      restorers.push(() => {
        if (ogCreated) ogEl.remove();
        else if (ogPrev?.content != null) ogEl.setAttribute("content", ogPrev.content);
      });

      const { el: twEl, created: twCreated, previous: twPrev } = setOrCreateMeta('meta[name="twitter:description"]', {
        name: "twitter:description",
        content: description,
      });
      restorers.push(() => {
        if (twCreated) twEl.remove();
        else if (twPrev?.content != null) twEl.setAttribute("content", twPrev.content);
      });
    }

    if (title) {
      const { el: ogEl, created: ogCreated, previous: ogPrev } = setOrCreateMeta('meta[property="og:title"]', {
        property: "og:title",
        content: title,
      });
      restorers.push(() => {
        if (ogCreated) ogEl.remove();
        else if (ogPrev?.content != null) ogEl.setAttribute("content", ogPrev.content);
      });

      const { el: twEl, created: twCreated, previous: twPrev } = setOrCreateMeta('meta[name="twitter:title"]', {
        name: "twitter:title",
        content: title,
      });
      restorers.push(() => {
        if (twCreated) twEl.remove();
        else if (twPrev?.content != null) twEl.setAttribute("content", twPrev.content);
      });
    }

    if (canonical) {
      const { el, created, previous } = setOrCreateLink("canonical", canonical);
      restorers.push(() => {
        if (created) el.remove();
        else if (previous != null) el.setAttribute("href", previous);
      });

      const { el: ogEl, created: ogCreated, previous: ogPrev } = setOrCreateMeta('meta[property="og:url"]', {
        property: "og:url",
        content: canonical,
      });
      restorers.push(() => {
        if (ogCreated) ogEl.remove();
        else if (ogPrev?.content != null) ogEl.setAttribute("content", ogPrev.content);
      });
    }

    if (ogImage) {
      const { el, created, previous } = setOrCreateMeta('meta[property="og:image"]', {
        property: "og:image",
        content: ogImage,
      });
      restorers.push(() => {
        if (created) el.remove();
        else if (previous?.content != null) el.setAttribute("content", previous.content);
      });
    }

    return () => {
      document.title = previousTitle;
      restorers.reverse().forEach((fn) => fn());
    };
  }, [title, description, canonical, ogImage]);
}
